import {baseTheme} from '~/shared/theme';
import {getPooledGroupTimesInfo} from '~/shared/utils/pooledGroupTimesInfo';
import {RestaurantFromSearch, RestaurantList} from '~/shared/store/models';
import {Carousel} from '~/shared/store/models/Carousel';
import RestaurantSortOptions from '~/shared/store/selectors/selectorUtils/restaurantSortOptions';
import {RestaurantsFiltersSortOptionsTypes} from '~/shared/consts/restaurantConsts';

import {selectRestaurantsMainListData} from '..';
import store from '../..';

type CollectionItem = {
  restaurantItem: RestaurantFromSearch;
  key: string;
  groupId: string;
  itemIndexInGroup: number;
};

const getCarouselGroupHeight = ({
  isMinMobile,
  isMinLargeMobile,
  isMinLargeTablet,
  isMinLargeDesktop,
  isHighlightInfo,
  isFirstCarousel,
}: {
  isMinMobile: boolean;
  isMinLargeMobile: boolean;
  isMinLargeTablet: boolean;
  isMinLargeDesktop: boolean;
  isHighlightInfo: boolean;
  isFirstCarousel: boolean;
}) => {
  if (isMinLargeDesktop) {
    return baseTheme.carouselHeight[isHighlightInfo ? 'largeDesktopHighlight' : 'largeDesktop'];
  }

  if (isMinLargeTablet) {
    return baseTheme.carouselHeight[isHighlightInfo ? 'largeTabletHighlight' : 'largeTablet'];
  }

  if (isMinLargeMobile) {
    return baseTheme.carouselHeight[isHighlightInfo ? 'largeMobileHighlight' : 'largeMobile'] +
        (isFirstCarousel ? baseTheme.carouselHeight.largeMobileOffset : 0);
  }

  if (isMinMobile) {
    return baseTheme.carouselHeight[isHighlightInfo ? 'mobileHighlight' : 'mobile'];
  }

  return baseTheme.carouselHeight[isHighlightInfo ? 'smallMobileHighlight' : 'smallMobile'];
};

type CarouselGroup = {
  carouselIndex: number;
  id: string;
  title: string;
  orderTypeViewTags: string;
  subtitle: string;
  iconUrl: string;
  groupId: string;
  isCarousel: boolean;
  key: string;
  height: number;
  items: CollectionItem[];
  highlightInfo: Carousel['highlightInfo'] | null;
};

const makeCarouselGroups = (
  collectionsList: Carousel[] | undefined,
  filteredRes: RestaurantList | Record<string, never>,
  currentSort: RestaurantsFiltersSortOptionsTypes,
  isMinMobile: boolean,
  isMinLargeMobile: boolean,
  isMinLargeTablet: boolean,
  isMinLargeDesktop: boolean,
) => {
  if (!collectionsList) {
    return [];
  }

  const {compare} = RestaurantSortOptions.find(({id}) => id === currentSort) || {};

  return collectionsList.reduce<CarouselGroup[]>((acc, collection, carouselIndex) => {
    const {id, title, iconUrl, orderTypeViewTags, miniFeedDesc, name, highlightInfo} = collection;
    const groupId = name.toLowerCase();
    const restaurantsByCollectionOrder = collection.resIds.reduce<CollectionItem[]>((collectionItems, resId) => {
      const restaurant = filteredRes.restaurantsList.find(({restaurantId}) => restaurantId === resId);

      if (!restaurant) {
        return collectionItems;
      }

      return [...collectionItems, {
        restaurantItem: {...restaurant, orderTypeViewTags},
        key: `${id}_${restaurant.restaurantId}`,
        groupId,
        itemIndexInGroup: collectionItems.length,
      }];
    }, []);

    if (compare) {
      restaurantsByCollectionOrder.sort((a, b) => compare(a.restaurantItem, b.restaurantItem));
    }

    return [...acc, {
      carouselIndex,
      id,
      title,
      orderTypeViewTags,
      subtitle: miniFeedDesc,
      iconUrl,
      groupId,
      isCarousel: true,
      key: `${id}_carousel`,
      isFirstCarousel: !carouselIndex,
      height: getCarouselGroupHeight({
        isMinMobile,
        isMinLargeMobile,
        isMinLargeTablet,
        isMinLargeDesktop,
        isHighlightInfo: !!highlightInfo,
        isFirstCarousel: !carouselIndex,
      }),
      items: restaurantsByCollectionOrder,
      highlightInfo,
    }];
  }, []);
};

export default makeCarouselGroups;

// why its here :|
// TODO move it to somewhere more relevant.
export const getPooledRestaurantsTimeData = () => {
  const resListData = selectRestaurantsMainListData(store.getState());
  if (!Object.keys(resListData)?.length) {
    return null;
  }

  const {poolTimeStart, poolTimeEnd, poolTimeDelivery} = resListData;
  return getPooledGroupTimesInfo(poolTimeStart, poolTimeEnd, poolTimeDelivery);
};
